import { defineStore } from 'pinia'
import { useMainStore } from '~/store/main/main.store'
import { useNuxtApp } from '#app'
import { getAddressLocation } from '~/composible/getAddressLoc'
import type { AddressChain } from '~/api'

interface requestParamsInt {
  yandex_geo?: number
  google_geo?: number
  client_ip?: string
}
export const useCities = defineStore('cities', {
  state: () => ({
    currentCity: {},
    currentRegion: {},
    redirectObj: undefined as {} | undefined,
    streetPageLoading: false,
    softNoindex: null as boolean | null,
    requestParams: {} as requestParamsInt,
    needGetCity: false,
    allCities: null,
    allRegions: null,
    indexTopologies: [] as string[],
    house: {},
    addressAncestors: undefined as AddressChain | undefined,
  }),
  getters: {
    getRedirectObj: state => state.redirectObj,
    getCity: (state) => {
      let city = state.addressAncestors?.address_objects?.find(el => el.level === 'city')
      if (!city) city = state.addressAncestors?.address_objects?.find(el => el.level === 'street')
      return city
    },
    getDataStreet: (state) => {
      let street
      const city = state.addressAncestors?.address_objects?.find(el => el.level === 'city')
      if (city) street = state.addressAncestors?.address_objects?.find(el => el.level === 'street')
      return street
    },
    getRegion: state => state.currentRegion,
    getCertainty: state => state.addressAncestors?.certainty,
    getRequestParams: state => state.requestParams,
    getNeedGetCity: state => state.needGetCity,
    getSoftNoindex: state => state.softNoindex,
    getStreetPageLoading: state => state.streetPageLoading,
    getAllCities: state => state.allCities,
    getAllRegions: state => state.allRegions,
    getIndexTopologies: state => state.indexTopologies,
    getHouse: state => state.house,
    getHouseId: state => state.house?.houseId,
    getHouseUrl: state => state.house?.autoGeneratedUrl,
    getFlat: state => state.house?.flat,
    getHouseHash: state => state.house?.hash,
    getHouseType: state => state.house?.houseType,
    getAddressAncestors: state => state.addressAncestors,
    getAddressText: (state) => {
      const indexRoot = state.addressAncestors?.address_objects?.find(
        el => el.url_root,
      )
        ? state.addressAncestors.address_objects.indexOf(
          state.addressAncestors.address_objects.find(
            el => el.url_root,
          )!,
        ) + 1
        : state.addressAncestors?.address_objects.length || undefined
      const currentArea = state.addressAncestors?.address_objects?.find(
        el => el.level === 'region',
      )
      const cityIndex = state.addressAncestors?.address_objects?.find(
        el => el.level === 'city',
      )
        ? state.addressAncestors.address_objects.indexOf(
            state.addressAncestors.address_objects.find(
              el => el.level === 'city',
            )!,
          )
        : state.addressAncestors?.address_objects?.find(
          el => el.level === 'street',
        )
          ? state.addressAncestors.address_objects.indexOf(
              state.addressAncestors.address_objects.find(
                el => el.level === 'street',
              )!,
            )
          : state.addressAncestors?.address_objects?.length || undefined

      const addressObjects
        = state.addressAncestors?.address_objects
          .slice(0, indexRoot)
          .reverse()
          ?.filter(el => el.level !== 'area')
          .map(
            el =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name + '\u00A0'
                : '') + (el.name ? el.name : ''),
          ) || []
      const addressObjectsStreets
        = state.addressAncestors?.address_objects
          .slice(0)
          .reverse()
          ?.filter(el => el.level === 'street')
          .map(
            el =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name + '\u00A0'
                : '') + (el.name ? el.name : ''),
          ) || []

      const cityUrl = useCities().getCity?.url || ''

      const house = state.house?.autoGeneratedUrl ? state.house : ''

      const fullAddress
        = addressObjects?.join(', ')
          + (house ? ', ' + house.houseNumber : '')
          + (house?.flat ? ', кв. ' + house.flat : '')
      const street = addressObjects?.join(', ')
      const streetOnly = addressObjectsStreets.join(', ')
      const houseOnly = house?.houseNumber

      const houseAddressWF
        = state.addressAncestors?.address_objects
          .slice(0, cityIndex || 1)
          .reverse()
          .map(
            el =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name + '\u00A0'
                : '') + (el.name ? el.name : ''),
          )
          .join(', ')
          + (house ? ', ' + house.houseNumber : '')

      const houseAddress
        = state.addressAncestors?.address_objects
          .slice(0, cityIndex || 1)
          .reverse()
          .map(
            el =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name + '\u00A0'
                : '') + (el.name ? el.name : ''),
          )
          .join(', ')
          + (house ? ', ' + house.houseNumber : '')
          + (house?.flat ? ', кв. ' + house.flat : '')

      const cityObjects = state.addressAncestors?.address_objects
        .slice(cityIndex ? cityIndex + 1 : 1, indexRoot)
        .reverse()
        ?.filter(el => el.level !== 'area')
      const cityAddress = addressObjects
        ? addressObjects
            ?.filter(el => el.level !== 'area')
            .map(
              el =>
                (el.short_name && typeof el.short_name === 'string'
                  ? el.short_name + '\u00A0'
                  : '') + (el.name ? el.name : ''),
            )
            .join(', ')
        : ''
      const cityArea = cityObjects?.length
        ? ', '
        + cityObjects.map(
          el =>
            (el.short_name && typeof el.short_name === 'string'
              ? el.short_name + '\u00A0'
              : '') + (el.name ? el.name : ''),
        )
        : ''

      const currentCity = state.addressAncestors?.address_objects.slice(
        cityIndex,
        indexRoot,
      )?.[0]
      const cityOnly = currentCity
        ? (cityObjects?.length
            ? cityObjects.map(el =>
                el.short_name && typeof el.short_name === 'string'
                  ? el.short_name + '\u00A0'
                  : '',
              )
            : '')
          + currentCity?.name.replace(/\s/g, '\u00A0')
        : ''
      const areaOnly = currentArea ? currentArea.full_name : cityOnly
      const cityOnlyLoc
        = currentCity && currentCity?.morph_name?.['где']
          ? currentCity?.morph_name?.['где']
            ?.replace(/\s/g, '\u00A0')
            + cityArea
          : currentCity
            ? (currentCity?.short_name && !currentCity?.name_loc
                ? currentCity?.short_name + ' '
                : '')
              + (currentCity?.name_loc
                ? currentCity?.name_loc
                  .replace(/\s/g, '\u00A0')
                  + cityArea
                : currentCity?.name
                    .replace(/\s/g, '\u00A0')
              )
            : ''
      const areaOnlyLoc = currentArea?.morph_name?.['где']
        ? currentArea?.morph_name?.['где']
            ?.replace(/\s/g, '\u00A0')

        : cityOnlyLoc
      const cityOnlyPos
        = currentCity && currentCity?.morph_name?.['Р']
          ? currentCity?.morph_name?.['Р']
            ?.replace(/\s/g, '\u00A0')
            + cityArea
          : currentCity
            ? (currentCity?.short_name && !currentCity?.name_loc
                ? currentCity?.short_name + ' '
                : '')
              + (currentCity?.name_loc
                ? currentCity?.name_loc
                  .replace(/\s/g, '\u00A0')
                  + cityArea
                : currentCity?.name
                    .replace(/\s/g, '\u00A0')
              )
            : ''
      const areaOnlyPos = currentArea?.morph_name?.['Р']
        ? currentArea?.morph_name?.['Р']
            ?.replace(/\s/g, '\u00A0')

        : cityOnlyPos

      return {
        cityIndex,
        indexRoot,
        currentArea,
        areaOnly,
        areaOnlyLoc,
        areaOnlyPos,
        cityArea,
        cityObjects,
        houseAddressWF,
        houseAddress,
        house,
        fullAddress,
        addressObjects,
        cityAddress,
        street,
        streetOnly,
        houseOnly,
        cityOnly,
        cityOnlyLoc,
        cityOnlyPos,
        cityUrl,
        currentCity,
      }
    },
  },
  actions: {
    async getAncestorsChainForAddress(fiasId: string) {
      const mainStore = useMainStore()
      try {
        const response = await $fetch(`${mainStore.apiDomain}/locations/address-objects/${fiasId}/ancestors/`, {
          method: 'GET',
          params: {
            include_self: true,
            morph: true,
            site_id: import.meta.env.VITE_SITE_ID,
          },
        })

        return this.getAddressObjectsFromResponse(response)
      }
      catch (e) {
        throw this.createErrorFromResponse(e)
      }
    },

    getAddressObjectsFromResponse(response) {
      let street
      let city = this.findAddressObject(response, 'city')
      if (
        !city
        && response?.address_objects?.filter(el => el.level === 'street')
          ?.length > 1
      ) {
        city = response?.address_objects
          .slice()
          .reverse()
          .find(el => el.level === 'street')
        street = response?.address_objects.find(
          address => address.level === 'street',
        )
      }
      else if (!city) city = this.findAddressObject(response, 'street')
      else street = this.findAddressObject(response, 'street')

      this.indexTopologies = response?.indexed_topologies

      return { city, street, res: response }
    },

    findAddressObject(response, level: string) {
      return response?.address_objects.find(el => el.level === level)
    },

    createErrorFromResponse(error) {
      return createError({
        statusCode: error.response.status,
        fatal: true,
        message: error.message,
      })
    },

    async nuxtServerInit($route: any) {
      try {
        this.redirectObj = undefined
        const provider = process.env.PROVIDER_SLUG
        const providerId = process.env.PROVIDER_ID ? +process.env.PROVIDER_ID : undefined
        const mainStore = useMainStore()
        const $nuxt = useNuxtApp()
        const getBranch = import.meta.env.VITE_BRANCH

        if ($route.path[$route.path.length - 1] !== '/') {
          return createError({
            statusCode: 404,
            fatal: true,
            message: 'not found',
          })
        }

        const b2b = $route.name === 'city-business' || $route.name === 'personal-business-slug' || $route.name === 'personal-done'

        const cityFromCookie = useCookie('currentCity')
        const addressFromCookie = useCookie(b2b ? 'b2bAddress' : 'currentAddress')
        const abTestCookie = useCookie('ab_test')
        const abTestCookie2 = useCookie('ab_test_2')
        const abTestCookie3 = useCookie('ab_test_3')
        const { 'x-real-ip': xRealIp } = useRequestHeaders()
        const reqHeaders = useRequestHeaders()
        const {
          'x-ab-test': abTest,
          host,
          'x-forwarded-host': newHost,
          'x-utm-state': xUtm,
          'if-modified-since': ifModifiedSince,
          'user-agent': userAgent,
          referer,
        } = useRequestHeaders()
        const domain = newHost || (import.meta.dev ? process.env.MAIN_DOMAIN : host)
        const apiDomain = process.env.VITE_NUXT_ENV_BACKEND_API_ROOT_URL
        mainStore.$patch({
          mainDomain: process.env.MAIN_DOMAIN,
          domain,
          reqHeaders,
          apiDomain,
        })
        const redirect = $nuxt.ssrContext?.event.res
        const partnerId = useCookie('partner_id')

        const numberPromise = async (val?: number) => {
          await mainStore.fetchPhoneNumber(val)
        }

        let respSiteOptions: any
        const respPromise = async () => {
          try {
            const abCookie = [abTest || abTestCookie.value, abTestCookie2.value, abTestCookie3.value].filter(el => !!el).join(',')
            respSiteOptions = await $fetch(apiDomain + '/site-options/', {
              method: 'GET',
              headers: {
                'authorization': 'Bearer ' + process.env.AUTH_TOKEN,
                'if-modified-since': ifModifiedSince,
                'X-Utm-State': xUtm,
                'user-agent': userAgent,
                referer,
              },
              params: {
                url: 'https://' + domain + $route.fullPath,
                branch: getBranch,
                site_version: abCookie || undefined,
              },
            })
          }
          catch (e) {
            console.error(e)
            return
          }
        }

        mainStore.$patch({
          disabledPortal: $nuxt.$device.isDesktop,
          disabledMobile: $nuxt.$device.isDesktop,
        })

        const queries = new URLSearchParams($route.fullPath.split('?')[1])
        for (const query of queries.entries()) {
          if (
            query[0] === 'deviceuse__device'
            || query[0] === 'tv_box_count'
            || query[0] === 'service__service_type'
            || query[0] === 'sort'
          ) {
            this.redirectObj = {
              url: $route.path,
            }
            return
          }
          if (query[0] === 'yandex_geo')
            this.requestParams.yandex_geo = +query[1] || undefined
          if (query[0] === 'google_geo')
            this.requestParams.google_geo = +query[1] || undefined
          if (query[0] === 'partner_id') partnerId.value = query[1]
        }

        if (xRealIp) {
          this.requestParams.client_ip = xRealIp
          mainStore.$patch({
            clientIp: xRealIp,
          })
        }

        const diNotProviderCookie = useCookie('diNotProvider')
        const oldClientCookie = useCookie('oldClient')
        const providerClientCookie = useCookie('providerClient')
        const addressWOTariffsCookie = useCookie('addressWOTariffs')

        if ($route.name === 'quiz-complete') {
          this.redirectObj = {
            url: '/quiz/',
            code: 301,
          }
          return
        }
        else if (
          (provider && $route.path.includes('tariffs/')
            && $route.name !== 'city-tariffs-id')
          || (/\/providers\/[^/]*\/tariffs\//i.test($route.path)
            && $route.name !== 'city-providers-provider-tariffs-id'
          )
        ) {
          const url = $route.fullPath.replace(/\/tariffs/gi, '')
          this.redirectObj = {
            url: url,
            code: 301,
          }
        }
        else if (
          (/providers-provider.*/i.test($route.name)
            && $route.name !== 'city-providers-provider-tariffs-id'
            && !diNotProviderCookie.value)
        ) {
          mainStore.$patch({
            diProvider: true,
          })
        }

        const localRoute = ('/' + ($route.params.city === '' ? '' : $route.params.city?.filter(el => !!el).join('/') || '') + ($route.params.street ? '/' + $route.params.street : '')).replace(/^\/tariffs.*|^\/house-tariffs.*|^\/business.*|^\/reviews.*|^\/for-house.*/, '/')

        let loc: any
        const { getAddressLoc } = getAddressLocation()
        const promiseLoc = async () => {
          loc = await getAddressLoc({
            url: $route.params['slug'] && ($route.name?.toString().includes('personal-address-slug') || $route.name === 'personal-business-slug')
              ? $route.params['slug']
                  .filter((el: string) => !!el)
                  .join('/')
              : $route.query.houseUrl
                ? $route.query.houseUrl
                : !$route.params['slug'] ? addressFromCookie.value || undefined : undefined,
            houseUrl: $route.params['slug'] && $route.name === 'city-address-slug'
              ? $route.params['city'].join('/') + '/' + $route.params['slug'].replace(/-dom-/g, '/')
              : undefined,
          })
        }
        await Promise.all([promiseLoc(), respPromise(), numberPromise(providerId || 0)])

        if (respSiteOptions?.last_modified) {
          redirect?.setHeader(
            'Last-Modified',
            new Date(respSiteOptions?.last_modified).toUTCString(),
          )
        }
        if (respSiteOptions?.not_modified) {
          this.redirectObj = {
            url: $route.fullPath,
            code: 304,
          }
          return
        }

        const config = respSiteOptions?.options.config as any

        mainStore.$patch({
          oldClient: !!oldClientCookie.value || false,
          providerClient: providerClientCookie.value || undefined,
          addressWOTariffs: !!addressWOTariffsCookie.value || false,
          automation: config?.automation,
          quizBtnShow: config?.quiz_btn_show,
          rtSpeedtest: config?.show_rt_speedtest,
          btn60Show: config?.btn_60sec || config?.btn60sec || config?.btn60sec_test,
          loaderText: config?.loader_text,
          loaderTime: config?.loader_time,
          segmentation: config?.segmentation || false,
          woFilter: config?.address_wo_filter,
          domainConfig: provider ? respSiteOptions?.options : undefined,
          metrikaId: respSiteOptions?.options?.yandex_id,
          uaId: respSiteOptions?.options?.google_id,
          providerId: providerId ? providerId + '' : undefined,
          woConsult: config?.wo_consult,
          hideNumber: config?.hide_phone_number || config?.test_37 || $route.query?.abtest === 'new37',
          automatizationDirect: config?.automatization_direct,
          reverseAutomatizationTabs: config?.reverse_automatization_tabs,
          showGreenBtn: config?.show_green_btn,
          popularSortVersion: config?.popular_sort_version,
          hideConnectionTime: config?.hide_connection_time,
          newTariffPrice: config?.new_tariff_price,
          supportLink: config?.support_link,
          test76: config?.test_76 || $route.query?.abtest === 'new76',
          test83: config?.test_83 || $route.query?.abtest === 'new83',
          test84: config?.test_84 || $route.query?.abtest === 'new84',
          test36: config?.test_36 || $route.query?.abtest === 'new36',
        })

        if (loc?.houseId || loc?.houseFiasId || (loc?.streetFiasId && loc?.houseNumber)) {
          const { city, res }
            = await this.getAncestorsChainForAddress(
              loc.fiasPath[loc.fiasPath.length - 1],
            )
          if (res?.redirect_status_code) {
            const oldUrl = $route.fullPath
            this.redirectObj = {
              url: oldUrl.replace(
                localRoute.slice(1),
                res.house?.url || res.address_objects[0].url,
              ),
              code: res.redirect_status_code,
            }
          }

          if (!$route.name?.toString().includes('personal-address-slug') && $route.name !== 'personal-business-slug' && $route.name !== 'city-address-slug' && ((this.house?.fiasPath && !this.house?.fiasPath?.includes(city.fias_id)) || (localRoute?.length > 2 && !localRoute.includes(city.url)))) {
            this.house = {}
            const responce = await $fetch(`${apiDomain}/locations/address-objects/chain-by-geo/`, {
              method: 'GET',

              params: {
                with_available_smart_filters: false,
                morph: true,
                url_geo: localRoute.replace(/^\/undefined/, ''),
                site_id: import.meta.env.VITE_SITE_ID,
                ...this.requestParams,
              },
            })

            const { res } = this.getAddressObjectsFromResponse(responce)
            this.$patch({
              addressAncestors: res,
            })

            return
          }

          this.$patch({
            addressAncestors: res,
          })

          if ($route.query?.flat && /^\d*$/.test($route.query?.flat)) this.house.flat = $route.query.flat

          if (!provider && /^\/house-tariffs\//i.test($route.path)) {
            this.redirectObj = {
              url: `/personal/address/${loc.autoGeneratedUrl}/`,
              code: 302,
            }
          }
          else if (!provider && /^\/business\//i.test($route.path)) {
            const url = `/${city!.url}${$route.fullPath}`
            this.redirectObj = {
              url,
              code: 302,
            }
          }

          return
        }
        else if (localRoute?.length > 1) {
          let responce
          let responceCheck
          try {
            responceCheck = await $fetch(`${apiDomain}/locations/address-objects/chain-by-geo/`, {
              method: 'GET',

              params: {
                with_available_smart_filters: false,
                morph: true,
                url_geo: localRoute.replace(/^\/undefined/, ''),
                site_id: import.meta.env.VITE_SITE_ID,
                ...this.requestParams,
              },
            })
          }
          catch (e) {
            return createError({
              statusCode: 404,
              fatal: true,
              message: 'not found',
            })
          }

          if (
            responceCheck.address_objects.length === 1
            && responceCheck.address_objects[0].level === 'region'
          ) {
            this.currentRegion = responceCheck.address_objects[0]
            if (this.house?.fiasPath && !this.house?.fiasPath?.includes(city.fias_id)) {
              this.house = {}
            }
            if (
              (cityFromCookie.value)
            ) {
              const { res } = await this.getAncestorsChainForAddress(
                cityFromCookie.value,
              )
              this.$patch({
                addressAncestors: res,
              })
            }
            return
          }
          else {
            responce = responceCheck
          }
          if (responce?.redirect_status_code) {
            const oldUrl = $route.fullPath
            this.redirectObj = {
              url: oldUrl.replace(
                localRoute.slice(1),
                responce.house?.url || responce.address_objects[0].url,
              ),
              code: responce?.redirect_status_code,
            }
          }
          const { city, res } = this.getAddressObjectsFromResponse(responce)

          if (responce.house) {
            return createError({
              statusCode: 410,
              fatal: true,
              message: 'not found',
            })
          }

          this.$patch({
            addressAncestors: res,
          })
          if (this.house?.fiasPath && !this.house?.fiasPath?.includes(city.fias_id)) {
            this.house = {}
          }

          return
        }
        else if (
          cityFromCookie.value
        ) {
          const { city, res } = await this.getAncestorsChainForAddress(
            cityFromCookie.value,
          )
          this.$patch({
            addressAncestors: res,
          })
          if (/^\/tariffs.*|^\/house-tariffs.*|^\/business.*|^\/reviews.*|^\/for-house.*/.test($route.path)) {
            let url = `/${city!.url}${$route.fullPath.replace(
              '/house-tariffs/',
              '/tariffs/',
            )}`
            if (provider) {
              url = url.replace(/\/tariffs/gi, '')
            }
            this.redirectObj = {
              url,
              code: 302,
            }
          }
          return
        }
        else if (this.requestParams.yandex_geo || this.requestParams.google_geo || /^\/tariffs.*|^\/house-tariffs.*|^\/business.*|^\/streets.*|^\/reviews.*|^\/for-house.*/.test($route.path)) {
          const responce = await $fetch(`${apiDomain}/locations/address-objects/chain-by-geo/`, {
            method: 'GET',

            params: {
              with_available_smart_filters: false,
              morph: true,
              provider: providerId || $route.params.provider,
              site_id: import.meta.env.VITE_SITE_ID,
              ...this.requestParams,
            },
          })

          const { city } = this.getAddressObjectsFromResponse(responce)
          let url = `/${city!.url}${$route.fullPath.replace(
            '/house-tariffs/',
            '/tariffs/',
          )}`
          if (provider) {
            url = url.replace(/\/tariffs/gi, '')
          }
          this.redirectObj = {
            url,
            code: 302,
          }

          return
        }
        else {
          this.needGetCity = true
          return
        }
      }
      catch (e) {
        const { 'x-real-ip': xRealIp, 'user-agent': userAgent,
          'x-forwarded-host': newHost, host } = useRequestHeaders()
        console.error('catch this')
        console.error(e)
        console.error('real status code: ' + e.statusCode)
        console.error('newHost: ' + newHost)
        console.error('host: ' + host)
        console.error('url: ' + $route?.fullPath)
        console.error('isBot: ' + useNuxtApp()?.$device.isBot)
        console.error('ip: ' + xRealIp)
        console.error('userAgent: ' + userAgent)
        return createError({
          statusCode: e.statusCode === 410 ? 410 : 404,
          fatal: true,
          message: 'not found',
        })
      }
    },
  },
})
